import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {ComputedPageItem} from '@taopix/taopix-core/dist/types/types';
import {
  AlignCentreIcon,
  AlignMiddleIcon,
  Button,
  SpreadCentreIcon,
} from '@taopix/taopix-design-system';
import {
  AssetType,
  BoxFitMode,
  BoxType,
  BoxTypeShape,
  NumBool,
  PageConstants,
} from '@taopix/taopix-core';
import {useTaopix} from '../../../Context/TaopixContext';
import classNames from 'classnames';
import {useExperience} from '../../../Experience/ExperienceProvider';
import {useUiState} from '../../../Context/UserInterface/UserInterfaceContext';
import ToolPaletteSection from '../../../ToolPalette/ToolPaletteSection';
import PaletteInformationMessage from '../PaletteInformationMessage';

export type AlignToPageProps = {
  sectionKey: string;
  item?: ComputedPageItem;
};

const AlignToPage = ({sectionKey, item}: AlignToPageProps) => {
  const app = useTaopix();
  const ui = useUiState();
  const {t} = useTranslation();

  const [showSpreadButton, setShowSpreadButton] = useState<boolean>();
  const [notAvailableMessage, setNotAvailableMessage] = useState<string>();

  const stateManager = window.Taopix.Core.get('stateManager');

  const productLayout = stateManager.getLayout();

  const pageNumber = ui?.state?.pageNumber;

  const {experience} = useExperience();
  const {pictureBoxOptions, textBoxOptions, shapeBoxOptions, stickerOptions} = experience.EDITOR;

  const [showSection, setShowSection] = useState<boolean>(false);
  const [disableSection, setDisableSection] = useState<boolean>(false);

  useEffect(() => {
    if (
      pageNumber !== PageConstants.FrontCoverPageNumber &&
      pageNumber !== PageConstants.BackCoverPageNumber
    ) {
      setShowSpreadButton(true);
    } else {
      setShowSpreadButton(false);
    }
  }, [ui?.state?.pageNumber]);

  const isActiveInExperience = (item: ComputedPageItem) => {
    const boxType = item.type;
    // Return whether or not this option is enabled in the experience for the selected item
    if (
      boxType === BoxType.Image &&
      item?.asset?.assetType !== AssetType.Background &&
      item?.asset?.assetType !== AssetType.ScrapbookImage
    ) {
      return pictureBoxOptions.alignToPage;
    }
    if (boxType === BoxType.Text) {
      return textBoxOptions.alignToPage;
    }
    if (
      boxType === BoxType.Shape &&
      [BoxTypeShape.Line, BoxTypeShape.Oval, BoxTypeShape.Polygon, BoxTypeShape.Rect].includes(
        item?.shape
      )
    ) {
      return shapeBoxOptions.alignToPage;
    }
    if (AssetType.ScrapbookImage === item.hiresfiletype) {
      return stickerOptions.alignToPage;
    } else {
      return false;
    }
  };

  const isProtected = (item: ComputedPageItem) => {
    // Return whether the item is protected or not
    return (
      NumBool.True === item?.protecthorizontalposition ||
      NumBool.True === item?.protectverticalposition
    );
  };

  useEffect(() => {
    // This is an advanced option, so check whether advanced editing is on
    const canShowAdvancedOptions = ui.state.canEditPageLayout;

    // If there is a selected item
    if (item && item.ref !== -1) {
      // Show or hide this section based on the relevant experience and advanced editing configuration
      setShowSection(isActiveInExperience(item) && canShowAdvancedOptions);
      // Enable or disable this section based on the item's protection
      setDisableSection(
        isProtected(item) || (item.errors?.imageMissing as boolean) || ui.state.swapActive
      );
      // Set the message to 'not available' in case the section was hidden or disabled
      setNotAvailableMessage(t('toolpalette:str_OptionNotAvailableForSelectedItem'));
    } else {
      // If no item is selected, hide the section button
      setShowSection(false);
      // And show the 'please select an item' message
      setNotAvailableMessage(t('toolpalette:str_MessageSelectAnItemToViewOptions'));
    }
  }, [item?.ref, item?.asset, item?.asset?.assetType, item?.type, ui.state.swapActive]);

  const sectionButtonClasses = classNames('lg:py-md');

  const wrapperClasses = classNames(
    'flex',
    'flex-col',
    'px-md',
    'lg:flex-1',
    'space-y-sm',
    'sm:pb-md',
    'sm:h-mobilePaletteContentHeight',
    'overflow-y-auto'
  );

  if (
    ui.state.palette.section !== 'box-settings-palette' &&
    ui.state.palette.section !== `box-settings-palette/${sectionKey}`
  ) {
    return null;
  }

  return (
    <ToolPaletteSection
      id="alignToPagePalette"
      title={t('boxpalettes:str_TitleAlignToPage')}
      buttonIcon={<AlignCentreIcon />}
      sectionKey={sectionKey}
      buttonClasses={sectionButtonClasses}
      useFocusMode
      subSection={true}
      showButton={showSection}
      disabled={disableSection}
      buttonLayout="horizontal"
      parentselected={true}
    >
      <div className={wrapperClasses}>
        {!showSection || disableSection ? (
          // If the palette button has been hidden or disabled (by the experience, box protection or nothing is selected),
          // show the relevant 'unavailable' message in the palette in case the palette was left open when its status changed
          <PaletteInformationMessage message={notAvailableMessage} />
        ) : (
          // Only show the sub options if the palette is allowed in the experience and the content is not protected
          // PLEASE NOTE: The labels on these buttons are the opposite of their BoxFitMode to suit user's understanding of vertical/horizontal centring.
          <>
            <Button
              data-testid={'box-settings-aligntopage-vertical-button'}
              className={'w-full'}
              label={t('boxpalettes:str_LabelVerticalCentre')}
              buttonStyle={ui.state.isSmallScreen ? 'standard' : 'tertiary'}
              boxless={ui.state.isSmallScreen}
              startIcon={<AlignCentreIcon />}
              labelAlignment={'left'}
              size={'large'}
              onClick={() => {
                app.core
                  .dispatch('SHAPE.ALIGN_ITEM', {mode: BoxFitMode.HORIZONTAL})
                  .catch(error => {
                    console.error(error);
                  });
              }}
            />
            <Button
              data-testid={'box-settings-aligntopage-horizontal-button'}
              className={'w-full'}
              label={t('boxpalettes:str_LabelHorizontalCentre')}
              buttonStyle={ui.state.isSmallScreen ? 'standard' : 'tertiary'}
              boxless={ui.state.isSmallScreen}
              startIcon={<AlignMiddleIcon />}
              labelAlignment={'left'}
              size={'large'}
              onClick={() => {
                app.core.dispatch('SHAPE.ALIGN_ITEM', {mode: BoxFitMode.VERTICAL}).catch(error => {
                  console.error(error);
                });
              }}
            />
            {showSpreadButton && productLayout.spreads === 1 && (
              <Button
                data-testid={'box-settings-aligntopage-spread-button'}
                className={'w-full'}
                label={t('boxpalettes:str_LabelSpreadCentre')}
                buttonStyle={ui.state.isSmallScreen ? 'standard' : 'tertiary'}
                boxless={ui.state.isSmallScreen}
                startIcon={<SpreadCentreIcon />}
                labelAlignment={'left'}
                size={'large'}
                onClick={() => {
                  app.core.dispatch('SHAPE.ALIGN_ITEM', {mode: BoxFitMode.SPREAD}).catch(error => {
                    console.error(error);
                  });
                }}
              />
            )}
          </>
        )}
      </div>
    </ToolPaletteSection>
  );
};

export default AlignToPage;
